/**
 * Strong's and Hevy's CSV, read into one shape (`D201`).
 *
 * ── STRONG ─────────────────────────────────────────────────────────────────
 * `Date,Workout Name,Duration,Exercise Name,Set Order,Weight,Reps,Distance,
 * Seconds,Notes,Workout Notes,RPE` — one row per set; a workout is the rows
 * that share `Date` and `Workout Name`. The file carries no unit: `Weight` and
 * `Distance` are in whatever the person set Strong to, so the screen asks
 * (kg + km, or lb + mi). `Set Order` is a number, or `W` / `D` / `F` for a
 * warm-up, drop or failure set — the markers this app's own export writes.
 * `Date` is the person's wall clock, read as this device's local time.
 *
 * ── HEVY ───────────────────────────────────────────────────────────────────
 * `title,start_time,end_time,description,exercise_title,superset_id,
 * exercise_notes,set_index,set_type,weight_kg,reps,distance_km,
 * duration_seconds,rpe` — the units are in the column names (`weight_lbs` and
 * `distance_miles` in an imperial export), `set_type` is `normal`, `warmup`,
 * `dropset` or `failure`, and the times are `11 Mar 2024, 07:00` (or ISO).
 *
 * Nothing here talks to the catalogue: an exercise is still the SOURCE's
 * name, matched later (`match.ts`) where the person can see and fix it.
 */
import type { SetKind } from "@repo/contract/training";
import { headerIndex } from "./csv";

export type SourceApp = "hevy" | "strong";

/**
 * Strong's `W`/`D`/`F` and Hevy's `warmup`/`dropset`/`failure`, mapped onto
 * the words a logged set carries (`D209`) — the contract's own type, so a
 * mapping to any other word is a type error here and not a set every figure
 * silently reads as a working one.
 */
export type SourceSetKind = SetKind;

export interface SourceSet {
  readonly distanceMetres: number | null;
  readonly durationSeconds: number | null;
  readonly exerciseName: string;
  readonly kind: SourceSetKind | null;
  readonly loadKg: number | null;
  readonly reps: number | null;
  readonly rpe: number | null;
}

export interface SourceWorkout {
  readonly endedAt: Date;
  /** The source's own key for the workout: its start as written, and its
   *  title. The server derives the session id from it (`workout-import.ts`),
   *  so the same file imported twice is the same history. */
  readonly importId: string;
  readonly sets: readonly SourceSet[];
  readonly startedAt: Date;
  readonly title: string;
}

/** Strong's file carries no unit; the person says which it was. */
export type StrongUnits = "imperial" | "metric";

const LB_TO_KG = 0.453_592_37;
const MI_TO_M = 1609.344;
const KM_TO_M = 1000;

const STRONG_COLUMNS = ["date", "workout name", "exercise name", "set order"];
const HEVY_COLUMNS = ["title", "start_time", "exercise_title", "set_type"];

export function detectSource(header: readonly string[]): SourceApp | null {
  const columns = headerIndex(header);
  if (HEVY_COLUMNS.every((name) => columns.has(name))) {
    return "hevy";
  }
  if (STRONG_COLUMNS.every((name) => columns.has(name))) {
    return "strong";
  }
  return null;
}

/** A number in either decimal convention ("62.5", "62,5"); `null` for blank
 *  or not a number. */
export function parseNumber(raw: string | undefined): number | null {
  const text = (raw ?? "").trim().replace(",", ".");
  if (text === "") {
    return null;
  }
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** Positive or `null`: a zero load is "no load", zero reps "no reps". */
function positive(value: number | null): number | null {
  return value !== null && value > 0 ? value : null;
}

function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

/** The server's RPE is in (0, 10]; anything else the source wrote is dropped
 *  rather than refused. */
function rpeOf(raw: string | undefined): number | null {
  const value = parseNumber(raw);
  return value !== null && value > 0 && value <= 10 ? value : null;
}

const LOCAL_DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;
const HEVY_DATE_TIME = /^(\d{1,2}) ([A-Za-z]{3,}) (\d{4}),? (\d{1,2}):(\d{2})$/;
const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

/** A wall-clock time with no zone, read as this device's local time; an ISO
 *  string with a zone is taken as it says. `null` when unreadable. */
export function parseLocalTime(raw: string): Date | null {
  const text = raw.trim();
  const local = LOCAL_DATE_TIME.exec(text);
  if (local !== null) {
    const [, year, month, day, hour, minute, second] = local;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second ?? "0")
    );
  }
  const hevy = HEVY_DATE_TIME.exec(text);
  if (hevy !== null) {
    const [, day, monthName, year, hour, minute] = hevy;
    const month = MONTHS.indexOf((monthName ?? "").slice(0, 3).toLowerCase());
    if (month === -1) {
      return null;
    }
    return new Date(
      Number(year),
      month,
      Number(day),
      Number(hour),
      Number(minute)
    );
  }
  const iso = new Date(text);
  return Number.isNaN(iso.getTime()) ? null : iso;
}

const DURATION_PART = /(\d+)\s*(h|m|s)/g;

/** Strong's `1h 5m`, `45m`, `30s` — in seconds; `null` when it says none. */
export function parseStrongDuration(raw: string | undefined): number | null {
  let seconds = 0;
  let found = false;
  for (const match of (raw ?? "").matchAll(DURATION_PART)) {
    found = true;
    const [, digits, unit] = match;
    const amount = Number(digits);
    if (unit === "h") {
      seconds += amount * 3600;
    } else if (unit === "m") {
      seconds += amount * 60;
    } else {
      seconds += amount;
    }
  }
  return found ? seconds : null;
}

const STRONG_KINDS: Readonly<Record<string, SourceSetKind>> = {
  d: "drop",
  f: "failure",
  w: "warmup",
};

const HEVY_KINDS: Readonly<Record<string, SourceSetKind>> = {
  dropset: "drop",
  failure: "failure",
  warmup: "warmup",
};

/** A workout with no end of its own lasts one minute per set, at least 20. */
function fallbackEnd(start: Date, sets: number): Date {
  return new Date(start.getTime() + Math.max(20, sets) * 60_000);
}

interface Group {
  end: Date | null;
  readonly sets: SourceSet[];
  readonly start: Date;
  readonly startText: string;
  readonly title: string;
}

function groupsToWorkouts(groups: Iterable<Group>): SourceWorkout[] {
  return [...groups]
    .filter((group) => group.sets.length > 0)
    .map((group) => {
      const end =
        group.end !== null && group.end.getTime() > group.start.getTime()
          ? group.end
          : fallbackEnd(group.start, group.sets.length);
      return {
        endedAt: end,
        importId: `${group.startText}|${group.title}`,
        sets: group.sets,
        startedAt: group.start,
        title: group.title,
      };
    })
    .sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());
}

/** One row's cells, by lower-cased column name; `undefined` for a column the
 *  file does not have, `""` for one it has and left empty. */
type Cell = (name: string) => string | undefined;

/** What tells one app's rows apart: where the workout's key is, how a row's
 *  end is read, and how a row becomes a set. */
interface Layout {
  readonly end: (cell: Cell, start: Date) => Date | null;
  readonly exercise: string;
  readonly set: (cell: Cell, exerciseName: string) => SourceSet;
  readonly start: string;
  readonly title: string;
}

const text = (value: string | undefined) => (value ?? "").trim();

function roundOrNull(value: number | null): number | null {
  return value === null ? null : Math.round(value);
}

function readWith(
  rows: readonly (readonly string[])[],
  layout: Layout
): SourceWorkout[] {
  const [header, ...body] = rows;
  if (header === undefined) {
    return [];
  }
  const columns = headerIndex(header);
  const groups = new Map<string, Group>();
  for (const row of body) {
    const cell: Cell = (column) => {
      const at = columns.get(column);
      return at === undefined ? undefined : (row[at] ?? "");
    };
    const startText = text(cell(layout.start));
    const title = text(cell(layout.title));
    const name = text(cell(layout.exercise));
    const start = parseLocalTime(startText);
    if (start === null || name === "") {
      continue;
    }
    const key = `${startText}|${title}`;
    const group = groups.get(key) ?? {
      end: layout.end(cell, start),
      sets: [],
      start,
      startText,
      title,
    };
    groups.set(key, group);
    group.sets.push(layout.set(cell, name));
  }
  return groupsToWorkouts(groups.values());
}

function strongLayout(units: StrongUnits): Layout {
  const loadFactor = units === "imperial" ? LB_TO_KG : 1;
  const distanceFactor = units === "imperial" ? MI_TO_M : KM_TO_M;
  return {
    end: (cell, start) => {
      const duration = parseStrongDuration(cell("duration"));
      return duration === null
        ? null
        : new Date(start.getTime() + duration * 1000);
    },
    exercise: "exercise name",
    set: (cell, exerciseName) => {
      const load = positive(parseNumber(cell("weight")));
      const distance = positive(parseNumber(cell("distance")));
      return {
        distanceMetres:
          distance === null ? null : round(distance * distanceFactor, 1),
        durationSeconds: positive(roundOrNull(parseNumber(cell("seconds")))),
        exerciseName,
        kind: STRONG_KINDS[text(cell("set order")).toLowerCase()] ?? null,
        loadKg: load === null ? null : round(load * loadFactor, 2),
        reps: positive(roundOrNull(parseNumber(cell("reps")))),
        rpe: rpeOf(cell("rpe")),
      };
    },
    start: "date",
    title: "workout name",
  };
}

/** Hevy names its units in the header: `weight_lbs` without `weight_kg` is
 *  an imperial export, and the same for distance. */
function hevySet(cell: Cell, exerciseName: string): SourceSet {
  const imperialLoad = cell("weight_kg") === undefined;
  const imperialDistance = cell("distance_km") === undefined;
  const load = positive(
    parseNumber(cell(imperialLoad ? "weight_lbs" : "weight_kg"))
  );
  const distance = positive(
    parseNumber(cell(imperialDistance ? "distance_miles" : "distance_km"))
  );
  return {
    distanceMetres:
      distance === null
        ? null
        : round(distance * (imperialDistance ? MI_TO_M : KM_TO_M), 1),
    durationSeconds: positive(
      roundOrNull(parseNumber(cell("duration_seconds")))
    ),
    exerciseName,
    kind: HEVY_KINDS[text(cell("set_type")).toLowerCase()] ?? null,
    loadKg:
      load === null ? null : round(imperialLoad ? load * LB_TO_KG : load, 2),
    reps: positive(roundOrNull(parseNumber(cell("reps")))),
    rpe: rpeOf(cell("rpe")),
  };
}

const HEVY_LAYOUT: Layout = {
  end: (cell) => parseLocalTime(text(cell("end_time"))),
  exercise: "exercise_title",
  set: hevySet,
  start: "start_time",
  title: "title",
};

export function readStrong(
  rows: readonly (readonly string[])[],
  units: StrongUnits
): SourceWorkout[] {
  return readWith(rows, strongLayout(units));
}

export function readHevy(
  rows: readonly (readonly string[])[]
): SourceWorkout[] {
  return readWith(rows, HEVY_LAYOUT);
}
